import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useCartSnapshot } from "../hooks/useCart";
import "../style/mobile_tablets.css";
import { routePaths } from "../constants/routes";
import { addToCart } from "../services/cartService";
import { fetchProducts } from "../services/commerceApi";

const CATEGORY_BANNERS = {
  "Phones & Tablets": {
    title: "Phone Deals",
    subtitle: "Discover mobile devices ready for automation practice",
  },
  "TV & Audio": {
    title: "Home Entertainment",
    subtitle: "Regression-ready TV and audio catalog from the database",
  },
  Fashion: {
    title: "Fashion Picks",
    subtitle: "Large category data set for search, sort, and browse scenarios",
  },
  Appliances: {
    title: "Appliance Offers",
    subtitle: "Use these SKUs for browsing, sorting, and product detail testing",
  },
};

const FASHION_PROMO_BANNERS = [
  {
    id: "fashion-banner-1",
    title: "Fresh Drop",
    subtitle: "Trending edits for everyday style",
    image:
      "https://rukminim2.flixcart.com/fk-p-flap/1600/780/image/583031cfd4571e23.png?q=90",
  },
  {
    id: "fashion-banner-2",
    title: "Menswear Deals",
    subtitle: "Sharp casuals, polos, and weekend fits",
    image:
      "https://rukminim2.flixcart.com/fk-p-flap/1600/780/image/34e3d876f03f2226.jpg?q=90",
  },
  {
    id: "fashion-banner-3",
    title: "Ethnic Focus",
    subtitle: "Kurtis, sets, and occasion-ready looks",
    image:
      "https://rukminim2.flixcart.com/fk-p-flap/1600/780/image/744d9eb7c8c79eab.png?q=90",
  },
  {
    id: "fashion-banner-4",
    title: "Top Picks",
    subtitle: "Dresses, tops, and style essentials",
    image:
      "https://rukminim2.flixcart.com/fk-p-flap/1600/780/image/ed31337b2b19e358.png?q=90",
  },
];

const FASHION_ICON_CATEGORIES = [
  { label: "Trendy", emoji: "T" },
  { label: "Shirts, Tees", emoji: "S" },
  { label: "Jeans", emoji: "J" },
  { label: "Sports Shoes", emoji: "SS" },
  { label: "Watches", emoji: "W" },
  { label: "Kids' Clothing", emoji: "K" },
  { label: "Luggage", emoji: "L" },
  { label: "Formal Wear", emoji: "F" },
  { label: "Casual Shirts", emoji: "C" },
  { label: "Sneakers", emoji: "SN" },
  { label: "Ethnic Sets", emoji: "E" },
  { label: "Sarees", emoji: "SA" },
];

export default function PhonesTabletsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "Phones & Tablets";

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("featured");
  const { cartIds, syncCartSnapshot } = useCartSnapshot();

  const bannerContent = useMemo(
    () =>
      CATEGORY_BANNERS[activeCategory] || {
        title: activeCategory,
        subtitle: "Database-backed catalog products for browse and filtering demos",
      },
    [activeCategory]
  );

  useEffect(() => {
    let isMounted = true;

    async function loadProducts() {
      setLoading(true);
      try {
        const response = await fetchProducts({
          category: activeCategory,
          limit: 150,
        });

        if (isMounted) {
          setProducts(response);
        }
      } catch (error) {
        console.error("Unable to load category products", error);
        if (isMounted) {
          setProducts([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadProducts();

    return () => {
      isMounted = false;
    };
  }, [activeCategory]);

  const visibleProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filteredProducts = products.filter((product) => {
      if (!normalizedQuery) {
        return true;
      }

      return [product.name, product.brand, product.category, product.description]
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery);
    });

    const sortedProducts = [...filteredProducts];

    if (sortBy === "price-low") {
      sortedProducts.sort((first, second) => first.currentPriceRaw - second.currentPriceRaw);
    } else if (sortBy === "price-high") {
      sortedProducts.sort((first, second) => second.currentPriceRaw - first.currentPriceRaw);
    } else if (sortBy === "rating") {
      sortedProducts.sort((first, second) => Number(second.rating) - Number(first.rating));
    } else {
      sortedProducts.sort((first, second) => {
        if (second.featured !== first.featured) {
          return Number(second.featured) - Number(first.featured);
        }
        return Number(second.flashSale) - Number(first.flashSale);
      });
    }

    return sortedProducts;
  }, [products, query, sortBy]);

  const isFashionCategory = activeCategory === "Fashion";

  const handleAddToCart = (event, product) => {
    event.preventDefault();
    event.stopPropagation();

    if (cartIds.has(String(product.id))) {
      navigate(routePaths.cart);
      return;
    }

    addToCart(product);
    syncCartSnapshot();
    toast.success(`${product.name} added to cart.`);
  };

  const fashionCuratedGroups = useMemo(() => {
    if (!isFashionCategory) {
      return [];
    }

    return [
      {
        id: "fashion-trending",
        title: "Trending now",
        subtitle: "The strongest styles for search, filtering, and browse testing",
        items: visibleProducts.slice(0, 4),
      },
      {
        id: "fashion-budget",
        title: "Budget fashion steals",
        subtitle: "Lower-price picks that still exercise the full browsing flow",
        items: [...visibleProducts]
          .sort((first, second) => first.currentPriceRaw - second.currentPriceRaw)
          .slice(0, 4),
      },
      {
        id: "fashion-premium",
        title: "Premium spotlight",
        subtitle: "Higher-value products for promo and checkout validation",
        items: [...visibleProducts]
          .sort((first, second) => second.currentPriceRaw - first.currentPriceRaw)
          .slice(0, 4),
      },
    ];
  }, [isFashionCategory, visibleProducts]);

  return (
    <div className="mobile-tablets-page">
      <nav className="breadcrumb-nav">
        <NavLink to="/home" className="breadcrumb-item">
          Home
        </NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <NavLink to="/product" className="breadcrumb-item">
          Product Dashboard
        </NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <span className="breadcrumb-item active-page">{activeCategory}</span>
      </nav>

      <div className="call-to-order-bar">
        Browse-only product catalog for search, sort, category navigation, and product detail practice
      </div>

      <section className="black-friday-banner">
        <div className="banner-content">
          <div className="banner-left">
            <div className="jumia-logo">
              QA Coder <span className="star-icon">*</span>
            </div>
            <div className="black-friday-text">{activeCategory}</div>
            <div className="date-range">120+ products available for this category</div>
          </div>
          <div className="banner-right">
            <h2>{bannerContent.title}</h2>
            <p>{bannerContent.subtitle}</p>
            <span className="call-to-action-btn">Database Integrated</span>
          </div>
        </div>
      </section>

      {isFashionCategory ? (
        <section className="fashion-shell">
          <div className="fashion-hero-grid">
            {FASHION_PROMO_BANNERS.map((banner) => (
              <article className="fashion-banner-card" key={banner.id}>
                <img src={banner.image} alt={banner.title} loading="lazy" decoding="async" />
                <div className="fashion-banner-overlay">
                  <span>Fashion edit</span>
                  <h3>{banner.title}</h3>
                  <p>{banner.subtitle}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="fashion-strip-banner">
            <div>
              <strong>Fashion category experience</strong>
              <p>
                Inspired by large marketplace layouts with hero creatives, compact navigation, and
                merchandising shelves.
              </p>
            </div>
            <span>Style + ecommerce testing</span>
          </div>

          <div className="fashion-quick-grid">
            {FASHION_ICON_CATEGORIES.map((item) => (
              <div className="fashion-quick-card" key={item.label}>
                <div className="fashion-quick-icon">{item.emoji}</div>
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          {fashionCuratedGroups.map((group) => (
            <section className="fashion-curated-section" key={group.id}>
              <div className="fashion-curated-header">
                <div>
                  <h3>{group.title}</h3>
                  <p>{group.subtitle}</p>
                </div>
              </div>

              <div className="fashion-feature-grid">
                {group.items.map((product) => (
                  <NavLink
                    to={`/product-details/${product.id}`}
                    className="fashion-feature-card"
                    key={`${group.id}-${product.id}`}
                  >
                    <div className="fashion-feature-image">
                      <img
                        src={product.listingImageUrl || product.url}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="fashion-feature-copy">
                      <strong>{product.brand}</strong>
                      <h4>{product.name}</h4>
                      <p>{product.currentPriceFormatted}</p>
                    </div>
                  </NavLink>
                ))}
              </div>
            </section>
          ))}
        </section>
      ) : null}

      <section
        style={{
          maxWidth: "1200px",
          margin: "0 auto 18px",
          padding: "18px 20px",
          borderRadius: "14px",
          background: "#fff",
          boxShadow: "0 10px 24px rgba(15,23,42,0.08)",
          display: "grid",
          gap: "14px",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", justifyContent: "space-between" }}>
          <div>
            <strong style={{ color: "#0f172a", fontSize: "1.1rem" }}>{activeCategory} Catalog</strong>
            <p style={{ margin: "6px 0 0", color: "#64748b" }}>
              Search, sort, and validate database-backed products in a browse-only flow.
            </p>
          </div>
          <NavLink
            to="/flash-sales"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "12px 16px",
              borderRadius: "999px",
              background: "#ea580c",
              color: "#fff",
              textDecoration: "none",
              fontWeight: 700,
            }}
          >
            View Flash Sales
          </NavLink>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 220px", gap: "12px" }}>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by product, brand, or description"
            style={{
              minHeight: "46px",
              padding: "12px 14px",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
            }}
          />
          <select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
            style={{
              minHeight: "46px",
              padding: "12px 14px",
              borderRadius: "12px",
              border: "1px solid #cbd5e1",
              background: "#fff",
            }}
          >
            <option value="featured">Featured first</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Best rating</option>
          </select>
        </div>
      </section>

      <main className="product-grid">
        {loading && <p>Loading products...</p>}

        {!loading &&
          visibleProducts.map((product) => (
            <NavLink
              to={`/product-details/${product.id}`}
              className="product-card"
              key={product.id}
            >
              <span className="discount-badge">-{product.discount}%</span>
              <div className="product-image-container">
                <img
                  src={product.listingImageUrl || product.url}
                  alt={product.name}
                  className="product-img"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="product-info">
                <div className="rating">
                  <i className="fas fa-star"></i> {product.rating} ({product.reviewCount})
                </div>
                <h3 className="product-name">{product.name}</h3>
                <p className="current-price">{product.currentPriceFormatted}</p>
                <p className="original-price">{product.originalPriceFormatted}</p>
                <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                  <button
                    type="button"
                    onClick={(event) => handleAddToCart(event, product)}
                    style={{
                      flex: 1,
                      minHeight: "40px",
                      borderRadius: "10px",
                      border: "none",
                      background: "#1d4ed8",
                      color: "#fff",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {cartIds.has(String(product.id)) ? "Go to Cart" : "Add to Cart"}
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault();
                      navigate(routePaths.productDetails(product.id));
                    }}
                    style={{
                      flex: 1,
                      minHeight: "40px",
                      borderRadius: "10px",
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                      color: "#0f172a",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
            </NavLink>
          ))}
      </main>

      <div id="item-count" style={{ textAlign: "center", margin: "40px 0", color: "#666" }}>
        Showing {visibleProducts.length} products from {activeCategory}
      </div>
    </div>
  );
}
